import {
  message,
  Space,
  Typography,
  Avatar,
  Statistic,
  Divider,
  Button,
  Modal,
} from "antd";
import { UserOutlined, PlusOutlined } from "@ant-design/icons";
import { cn, getDayGreeting } from "../lib/utils";

import PageHeader from "../components/shared/PageHeader";
import BlueButton from "../components/shared/BlueButton";
import { getFileUrl } from "../api/file";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getDashboardSummary } from "../api/dashboard";
import { endOfMonth, startOfMonth, } from "date-fns";
import SummaryCard from "../components/summary/SummaryCard";
import AlertTile from "../components/summary/AlertTile";
import ClientTile from "../components/summary/ClientTile";
import WorklogNotificationTile from "../components/summary/WorklogNotificationTile";
import dayjs from "dayjs";
import LoadingModal from "../components/shared/LoadingModal";
import { EmptyView } from "../components/shared/EmptyView";
import { filterProjectInfoFromDate } from "../components/summary/CalendarSection/MonthlyCalendar";
import CalendarSection from "../components/summary/CalendarSection";
import { getWorklogActions } from "../api/worklog";

const { Title, Text } = Typography;

export default function SummaryView({ user }) {
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [summary, setSummary] = useState(null);
  const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(() => endOfMonth(new Date()));
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [worklogActions, setWorklogActions] = useState([]);

  const { activeEmployeeToday, activeEquipmentToday } = useMemo(() => {
    if (!summary) return { activeEmployeeToday: [], activeEquipmentToday: [] };

    const { todaysTasks } = summary;

    const { activeEmployeeForTheDay, activeEquipmentForTheDay } = todaysTasks.reduce((acc, task) => {
      acc.activeEmployeeForTheDay.push(task.assignee);
      acc.activeEquipmentForTheDay.push(task.equipment);
      return acc;
    }, {
      activeEmployeeForTheDay: [],
      activeEquipmentForTheDay: [],
    })

    return {
      activeEmployeeToday: activeEmployeeForTheDay,
      activeEquipmentToday: activeEquipmentForTheDay,
    };
  }, [summary]);

  const fetchSummary = useCallback(async () => {
    try {
      setIsLoading(true);
      const summary = await getDashboardSummary({
        from: startDate.toISOString(),
        to: endDate.toISOString(),
        todayDate: new Date().toISOString(),
      });
      setSummary(summary);

      // Fetch worklog actions (notifications)
      const actionsData = await getWorklogActions({
        page: 1,
        perPage: 50, // Fetch 50 items
        sortBy: 'createdAt',
        sortType: 'desc',
      });
      setWorklogActions(actionsData.results || []);

      setIsLoading(false);
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
      setIsLoading(false);
    }
  }, [endDate, startDate,]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const {
    topClients,
    numOfPipelineProjects,
    numOfInProgressProject,
    numOfCompletedProjects,
    userAttachmentsToBeExpired,
    equipmentAttachmentsToBeExpired,
  } = useMemo(() => {
    if (!summary) return {};

    const {
      numOfPipelineProjects,
      numOfInProgressProject,
      numOfCompletedProjects,
      userAttachmentsToBeExpired,
      equipmentAttachmentsToBeExpired,
    } = summary;


    const topClients = (summary.projects || [])
      .map((p) => p.client)
      .reduce((acc, client) => {
        if (acc.find((c) => c.id === client.id)) return acc;
        acc.push(client);
        return acc;
      }, []);

    return {
      topClients,
      numOfPipelineProjects,
      numOfInProgressProject,
      numOfCompletedProjects,
      userAttachmentsToBeExpired,
      equipmentAttachmentsToBeExpired,
    };
  }, [summary]);

  if (err) return "something went wrong";
  if (isLoading || !summary) return <LoadingModal />;

  return (
    <>
      <main className="space-y-6">
        <PageHeader title="Summary">
          <Space className={cn("h-[72px] w-full flex justify-between")}>
            <div className="flex">
              <Avatar
                src={user.avatar ? getFileUrl(user.avatar) : null}
                size={"large"}
                icon={
                  <UserOutlined className="h-full w-full flex text-4xl justify-center " />
                }
                className={cn("h-[72px] w-[72px] mr-2")}
              />
              <div className={cn("flex flex-col justify-center")}>
                <Title level={5}>
                  {getDayGreeting()}, {user.firstName}!
                </Title>
                <Text type="secondary">
                  {activeEmployeeToday.length} of your employees and{" "}
                  {activeEquipmentToday.length} of your equipments are active
                </Text>
              </div>
            </div>
            <Space>
              <Statistic
                title="Projects in pipeline"
                value={numOfPipelineProjects}
              />
              <Divider type="vertical" />
              <Statistic
                title="Projects in progress"
                value={numOfInProgressProject}
              />
              <Divider type="vertical" />
              <Statistic
                title="Projects completed"
                value={numOfCompletedProjects}
              />
            </Space>
          </Space>
        </PageHeader>

        <div className="px-6 flex flex-col gap-4">

          <div className="w-full space-x-6 h-[354px] flex">
            <SummaryCard
              title="Alerts"
              extra={<Button type="link" onClick={() => setShowAllAlerts(true)}>View All Alerts</Button>}
            >
              {(() => {
                const totalAlerts = (userAttachmentsToBeExpired || []).length + (equipmentAttachmentsToBeExpired || []).length;
                const maxAlertsToShow = Math.min(4, totalAlerts); // 4 or less

                // Counters for how many alerts to show from each array
                let userAlertsToShow = 0;
                let equipmentAlertsToShow = 0;

                if (totalAlerts > 0) {
                  // Distribute the alerts between user and equipment attachments
                  userAlertsToShow = Math.min(maxAlertsToShow, (userAttachmentsToBeExpired || []).length);
                  equipmentAlertsToShow = Math.max(0, maxAlertsToShow - userAlertsToShow);
                }

                return [
                  ...(userAttachmentsToBeExpired || [])
                    .slice(0, userAlertsToShow)
                    .map((attchment) => (
                      <AlertTile
                        key={attchment.id}
                        id={attchment.user.id}
                        entityName={`${attchment.user.firstName} ${attchment.user.lastName}`}
                        kind="users"
                        documentName={attchment.fileAttachment.name}
                        expireAt={dayjs(attchment.fileAttachment.expireAt).format("YYYY/MM/DD")}
                      />
                    )),
                  ...(equipmentAttachmentsToBeExpired || [])
                    .slice(0, equipmentAlertsToShow)
                    .map((attchment) => (
                      <AlertTile
                        key={attchment.id}
                        id={attchment.equipment.id}
                        entityName={attchment.equipment.name}
                        kind="equipments"
                        documentName={attchment.fileAttachment.name}
                        expireAt={dayjs(attchment.fileAttachment.expireAt).format("YYYY/MM/DD")}
                      />
                    )),
                ];
              })()}
            </SummaryCard>

            <SummaryCard
              title="Worklog Notifications"
              extra={<Button type="link" onClick={() => setShowAllNotifications(true)}>View All</Button>}
            >
              {(worklogActions || []).slice(0, 10).map((action) => (
                <WorklogNotificationTile key={action.id} action={action} />
              ))}
            </SummaryCard>

            <SummaryCard
              title="Top Clients"
              extra={<Button type="link" href="/clients">All Clients</Button>}
            >
              {(topClients || []).map((c) => {
                return <ClientTile key={c.id} client={c} />;
              })}
            </SummaryCard>
          </div>

          <div className="w-full space-x-6 bg-white py-3 flex gap-20 px-10">
            <div className="left leading-0">
              <Title level={5} className="m-0">
                Quick Actions:
              </Title>
            </div>
            <div className="right flex justify-center gap-5">
              <BlueButton
                type="default"
                icon={<PlusOutlined />}
                href="/users/form"
              >
                Add New User
              </BlueButton>
              <BlueButton
                type="default"
                icon={<PlusOutlined />}
                href={"/projects/form"}
              >
                Add New Project
              </BlueButton>
              <BlueButton
                type="default"
                icon={<PlusOutlined />}
                href={"/equipments/form"}
              >
                Add New Equipment
              </BlueButton>
              <BlueButton
                type="default"
                icon={<PlusOutlined />}
                href={"/clients/form"}
              >
                Add New Client
              </BlueButton>
            </div>
          </div>
        </div>

        <section className="px-6 relative">
          <CalendarSection equipments={summary.equipments} users={summary.users} projects={summary.projects} />
        </section>
      </main>

      <Modal title="All Alerts" open={showAllAlerts} footer={null} onCancel={() => setShowAllAlerts(false)}>
        <div className="min-h-72 max-h-72 overflow-y-scroll">
          {userAttachmentsToBeExpired.length === 0 && equipmentAttachmentsToBeExpired.length === 0 ? <EmptyView name={"Alerts"} /> : null}
          {(userAttachmentsToBeExpired || []).map((attchment, index) => (
            <div key={attchment.user.id + index}>
              <AlertTile
                id={attchment.user.id}
                entityName={
                  attchment.user.firstName + " " + attchment.user.lastName
                }
                kind="users"
                documentName={attchment.fileAttachment.name}
                expireAt={dayjs(attchment.fileAttachment.expireAt).format(
                  "YYYY/MM/DD"
                )}
              />
              <Divider className="p-0 m-0" />
            </div >
          ))}
          {(equipmentAttachmentsToBeExpired || [])
            .map((attchment, index) => (
              <div key={attchment.equipment.id + index}>
                <AlertTile
                  id={attchment.equipment.id}
                  entityName={attchment.equipment.name}
                  kind="equipments"
                  documentName={attchment.fileAttachment.name}
                  expireAt={dayjs(attchment.fileAttachment.expireAt).format(
                    "YYYY/MM/DD"
                  )}
                />
                <Divider className="p-0 m-0" />
              </div>
            ))}

        </div>
      </Modal >

      <Modal
        title="All Worklog Notifications"
        open={showAllNotifications}
        footer={null}
        onCancel={() => setShowAllNotifications(false)}
        width={700}
      >
        <div className="min-h-72 max-h-96 overflow-y-scroll">
          {worklogActions.length === 0 ? (
            <EmptyView name="Notifications" />
          ) : (
            worklogActions.map((action) => (
              <div key={action.id}>
                <WorklogNotificationTile action={action} />
                <Divider className="p-0 m-0" />
              </div>
            ))
          )}
        </div>
      </Modal>
    </>
  );
}
